
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface AddServicePopupProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
}

export default function AddServicePopup({ isOpen, onClose, userEmail }: AddServicePopupProps) {
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [additionalCans, setAdditionalCans] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const plans = [
    { id: 'one-time', name: 'One-Time Cleaning', price: 70, interval: 'one-time', desc: 'Perfect for trying our service' },
    { id: 'quarterly', name: 'Quarterly Cleaning', price: 35, interval: 'quarter', desc: 'Every 3 months (Save 50%!)' },
    { id: 'monthly', name: 'Monthly Cleaning', price: 20, interval: 'month', desc: 'Every month (Best Value!)' }
  ];

  const selectedPlanData = plans.find(p => p.id === selectedPlan);
  const totalPrice = (selectedPlanData?.price || 0) + (additionalCans * 10);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const response = await supabase.functions.invoke('create-checkout-session', {
        body: {
          planType: selectedPlan,
          additionalCans: additionalCans,
          successUrl: `${window.location.origin}/account?payment=success`,
          cancelUrl: `${window.location.origin}/account`,
        }
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to create checkout session');
      }

      const { url } = response.data;
      
      if (!url) {
        throw new Error('Failed to create checkout session');
      }

      window.location.href = url;
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={handleBackdropClick}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-teal-500 p-6 text-center relative">
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full w-8 h-8 flex items-center justify-center transition-colors cursor-pointer"
              aria-label="Close popup"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <div className="w-16 h-16 bg-white/20 rounded-full mx-auto flex items-center justify-center mb-3">
              <i className="ri-add-circle-line text-4xl text-white"></i>
            </div>
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
              Add New Service
            </h2>
            <p className="text-white/90 text-sm mt-1">Choose a plan to get started</p>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Plan Selection */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-3">
                <i className="ri-price-tag-3-line mr-2 text-green-500"></i>
                Select Your Plan
              </label>
              <div className="space-y-3">
                {plans.map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`w-full p-4 rounded-2xl border-2 font-medium transition-all cursor-pointer text-left relative ${
                      selectedPlan === plan.id
                        ? 'border-green-500 bg-green-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    {plan.id === 'monthly' && (
                      <div className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                        Best Value
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedPlan === plan.id ? 'border-green-500 bg-green-500' : 'border-gray-300'
                        }`}>
                          {selectedPlan === plan.id && (
                            <i className="ri-check-line text-white text-sm"></i>
                          )}
                        </div>
                        <div>
                          <div className="font-bold text-gray-900">{plan.name}</div>
                          <div className="text-xs text-gray-500">{plan.desc}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-black text-green-600" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                          ${plan.price}
                        </div>
                        <div className="text-xs text-gray-500">
                          {plan.interval === 'one-time' ? 'one-time' : `per ${plan.interval}`}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Additional Cans */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-3">
                <i className="ri-add-box-line mr-2 text-green-500"></i>
                Additional Cans ($10 each)
              </label>
              <div className="flex items-center justify-center gap-4 bg-gray-50 rounded-2xl p-4">
                <button
                  type="button"
                  onClick={() => setAdditionalCans(Math.max(0, additionalCans - 1))}
                  disabled={additionalCans === 0}
                  className={`w-10 h-10 rounded-full font-bold text-xl flex items-center justify-center cursor-pointer transition-all ${
                    additionalCans === 0
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-red-100 hover:bg-red-200 text-red-600'
                  }`}
                >
                  -
                </button>
                <div className="text-3xl font-black text-gray-900 w-12 text-center" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                  {additionalCans}
                </div>
                <button
                  type="button"
                  onClick={() => setAdditionalCans(additionalCans + 1)}
                  className="w-10 h-10 rounded-full bg-green-100 hover:bg-green-200 text-green-600 font-bold text-xl flex items-center justify-center cursor-pointer transition-all"
                >
                  +
                </button>
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">Base plan includes up to 2 cans</p>
            </div>

            {/* Price Summary */}
            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl p-4 border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600">Base Plan ({selectedPlanData?.name})</span>
                <span className="font-bold">${selectedPlanData?.price}</span>
              </div>
              {additionalCans > 0 && (
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600">{additionalCans} Additional Can{additionalCans > 1 ? 's' : ''}</span>
                  <span className="font-bold">+${additionalCans * 10}</span>
                </div>
              )}
              <div className="border-t border-dashed border-green-300 my-3"></div>
              <div className="flex items-center justify-between">
                <span className="text-lg font-bold text-gray-800">Total</span>
                <div className="text-right">
                  <div className="text-3xl font-black text-green-600" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                    ${totalPrice}
                  </div>
                  <div className="text-xs text-gray-500">
                    {selectedPlanData?.interval === 'one-time' ? 'one-time payment' : `per ${selectedPlanData?.interval}`}
                  </div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-100 text-red-700 px-4 py-3 rounded-xl text-sm font-medium">
                <i className="ri-error-warning-line mr-2"></i>
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-green-500 to-teal-500 text-white py-4 rounded-full font-bold hover:scale-105 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2"
              style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-xl"></i>
                  Processing...
                </>
              ) : (
                <>
                  <i className="ri-shopping-cart-line text-xl"></i>
                  Proceed to Checkout
                </>
              )}
            </button>

            <p className="text-xs text-center text-gray-500">
              You'll be redirected to our secure payment page
            </p>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
